/**
 * Activities Editor - TICKET ZKPVN1
 * 
 * Editor de actividades dentro de un módulo.
 */

import React, { useState } from 'react';
import { Plus, PlayCircle } from 'lucide-react';
import ActivityRow from './ActivityRow';
import ActivityModal from './ActivityModal';

const ActivitiesEditor = ({ moduleId, activities, onUpdateActivities }) => {
  const [activityModalOpen, setActivityModalOpen] = useState(false);
  const [editingActivity, setEditingActivity] = useState(null);

  // Generar slug
  const generateSlug = (text) => {
    return text
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, '')
      .replace(/\s+/g, '-');
  };

  // Crear actividad
  const handleCreateActivity = (activityData) => {
    const newActivity = {
      activityId: activityData.activityId || generateSlug(activityData.title),
      type: activityData.type,
      title: activityData.title,
      estimatedMinutes: activityData.estimatedMinutes,
      order: activities.length > 0 ? Math.max(...activities.map(a => a.order)) + 1 : 1,
      refId: activityData.refId || null,
      resource: activityData.resource || null
    };

    onUpdateActivities([...activities, newActivity]);
    setActivityModalOpen(false);
  };

  // Editar actividad
  const handleEditActivity = (activityData) => {
    const updatedActivities = activities.map(a =>
      a.activityId === editingActivity.activityId
        ? {
            ...a,
            title: activityData.title,
            type: activityData.type,
            estimatedMinutes: activityData.estimatedMinutes,
            refId: activityData.refId || null,
            resource: activityData.resource || null
          }
        : a
    );

    onUpdateActivities(updatedActivities);
    setActivityModalOpen(false);
    setEditingActivity(null);
  };

  // Eliminar actividad
  const handleDeleteActivity = (activityId) => {
    if (window.confirm('¿Seguro que deseas eliminar esta actividad?')) {
      onUpdateActivities(activities.filter(a => a.activityId !== activityId));
    }
  };

  // Reordenar actividad (subir)
  const handleMoveActivityUp = (activityId) => {
    const sortedActivities = [...activities].sort((a, b) => a.order - b.order);
    const index = sortedActivities.findIndex(a => a.activityId === activityId);
    if (index <= 0) return;

    const newActivities = [...sortedActivities];
    const temp = newActivities[index].order;
    newActivities[index].order = newActivities[index - 1].order;
    newActivities[index - 1].order = temp;

    onUpdateActivities(newActivities.sort((a, b) => a.order - b.order));
  };

  // Reordenar actividad (bajar)
  const handleMoveActivityDown = (activityId) => {
    const sortedActivities = [...activities].sort((a, b) => a.order - b.order);
    const index = sortedActivities.findIndex(a => a.activityId === activityId);
    if (index >= sortedActivities.length - 1) return;

    const newActivities = [...sortedActivities];
    const temp = newActivities[index].order;
    newActivities[index].order = newActivities[index + 1].order;
    newActivities[index + 1].order = temp;

    onUpdateActivities(newActivities.sort((a, b) => a.order - b.order));
  };

  const sortedActivities = [...activities].sort((a, b) => a.order - b.order);

  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
          <PlayCircle className="w-4 h-4" />
          Actividades
        </h4>
        <button
          onClick={() => {
            setEditingActivity(null);
            setActivityModalOpen(true);
          }}
          className="flex items-center gap-2 px-3 py-1.5 text-sm bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
        >
          <Plus className="w-3.5 h-3.5" />
          Agregar actividad
        </button>
      </div>

      {/* Lista de actividades */}
      {sortedActivities.length === 0 ? (
        <div className="text-center py-6 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
          <p className="text-sm text-gray-600 mb-2">
            Este módulo no tiene actividades
          </p>
          <button
            onClick={() => {
              setEditingActivity(null);
              setActivityModalOpen(true);
            }}
            className="inline-flex items-center gap-2 px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-3.5 h-3.5" />
            Crear primera actividad
          </button>
        </div>
      ) : (
        <div className="space-y-2">
          {sortedActivities.map((activity, index) => (
            <ActivityRow
              key={activity.activityId}
              activity={activity}
              isFirst={index === 0}
              isLast={index === sortedActivities.length - 1}
              onEdit={() => {
                setEditingActivity(activity);
                setActivityModalOpen(true);
              }}
              onDelete={() => handleDeleteActivity(activity.activityId)}
              onMoveUp={() => handleMoveActivityUp(activity.activityId)}
              onMoveDown={() => handleMoveActivityDown(activity.activityId)}
            />
          ))}
        </div>
      )}

      {/* Modal de actividad */}
      {activityModalOpen && (
        <ActivityModal
          activity={editingActivity}
          moduleId={moduleId}
          existingIds={activities.map(a => a.activityId)}
          onSave={editingActivity ? handleEditActivity : handleCreateActivity}
          onClose={() => {
            setActivityModalOpen(false);
            setEditingActivity(null);
          }}
        />
      )}
    </div>
  );
};

export default ActivitiesEditor;
